import type { OrganizationProductContext, ProductManifest } from "./types";

export type ProductShellState = "available" | "adapter-pending" | "not-bound" | "suspended" | "unavailable";

type BrowserLocation = {
  hostname: string;
  pathname: string;
};

const META_REVIEW_HOSTNAME = "app.staging.clickdialog.com";
const META_REVIEW_ORGANIZATION_PATH = "/o/staging-synthetic-lab";

function currentBrowserLocation(): BrowserLocation | undefined {
  if (typeof window === "undefined") return undefined;
  return { hostname: window.location.hostname, pathname: window.location.pathname };
}

export function isMetaReviewWorkspaceLocation(location: BrowserLocation | undefined = currentBrowserLocation()): boolean {
  if (!location || location.hostname !== META_REVIEW_HOSTNAME) return false;
  return location.pathname === META_REVIEW_ORGANIZATION_PATH || location.pathname.startsWith(`${META_REVIEW_ORGANIZATION_PATH}/`);
}

export function listedProducts(products: readonly OrganizationProductContext[], location: BrowserLocation | undefined = currentBrowserLocation()): readonly OrganizationProductContext[] {
  if (isMetaReviewWorkspaceLocation(location)) return [];
  return products.filter(({ manifest, access }) => manifest.status !== "disabled" && access.organizationProduct?.status !== "canceled");
}

export function productState(
  manifest: ProductManifest | undefined,
  product: OrganizationProductContext | undefined,
  runtimeAvailable = false,
  location: BrowserLocation | undefined = currentBrowserLocation(),
): ProductShellState {
  if (isMetaReviewWorkspaceLocation(location)) return "unavailable";
  if (!manifest || manifest.status === "disabled") return "unavailable";
  const organizationProduct = product?.access.organizationProduct;
  if (!product || !organizationProduct) return "not-bound";
  const { access } = product;
  if (organizationProduct.status === "suspended" || organizationProduct.status === "past_due" || access.denialReason === "binding_inactive") return "suspended";
  if (access.denialReason === "binding_missing") return "adapter-pending";
  if (!access.allowed) return "unavailable";
  return runtimeAvailable ? "available" : "adapter-pending";
}
